import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { InputIconType } from '../../core/model/input-icon-type';
import { LoginService } from '../../core/service/login/login.service';
import { EnterPressClassSelector } from '../../../components/directives/enter-press/enter-press.config';
import { NyzeBody } from '../../../components/nyze-animations/body/body';
import { ValidationService } from '../../../service/validation/validation.service';
import { SocialsIcon } from '../../../components/buttons/dn-social-btn/dn-social-btn.config';
import { DnBtnSize } from '../../../components/dn-primary-btn/dn-primary-btn.config';
import { Subscription } from 'rxjs';
import _get from 'lodash-es/get';
import { LoginResponse } from '../../core/model/emit.interface';
import { ConfigService } from '../../core/service/config.service';
import { CommonService } from '../../core/service/common.service';
import { ErrorCodes } from '../../core/model/error-codes.enum';
import { StatusCodes } from '../../core/model/status-codes.enum';
import { LoginFormIds, LoginRequest } from '../../core/model/login.interface';
import { NyzeStatus } from '../../core/model/nyze-body.interface';
import { DefaultNyze, ErrorNyze } from '../../core/constants/nyze.consts';
import { SignUpFormsIds } from '../../core/model/sign-up.interface';
import { GoogleService } from '../../core/service/oauth2/google.service';
import { Office365Service } from '../../core/service/oauth2/office365.service';

import { GoogleAnalyticsSource } from '../../core/model/analytics.enum';

@Component({
    selector: 'dn-login',
    templateUrl: './dn-login-page.component.html',
    styleUrl: './dn-login-page.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class DnLoginPageComponent implements OnInit, OnDestroy {
  iconType;
  loginForm: UntypedFormGroup;
  email;
  password;
  rememberMe: boolean;
  emailErrorMessage = 'Please enter a valid email address';
  loginBtnText = 'Log In Now';
  passwordMessage = 'Please use at least 1 letter & 1 number, 8-20 characters';

  errorMessage;
  redirectUrl: any;
  mainBtnDisabled = false;
  loginSubscription: Subscription;
  allSubscriptions = [];
  imagesUrl: any;
  mainConfig: any;
  displayContactSupport = false;
  contactSupportLink: string;
  googleApiKey;
  nyze: NyzeStatus = DefaultNyze;
  enterPressClass = EnterPressClassSelector;
  formIds = LoginFormIds;
  loading = false;
  socialsIcon = SocialsIcon;
  buttonSize = DnBtnSize;
  openedDialog;

  @Input() displayGoToSignUp = true;
  @Input() deviceId = undefined;
  @Input() verificationId: string;
  // @Input() twoFactorAuthToken = undefined;

  @Output() goToSignUp = new EventEmitter();
  @Output() loginResponse = new EventEmitter();
  @Output() goToForgotPassword = new EventEmitter();
  @Output() loginClicked = new EventEmitter();

  constructor(private readonly loginService: LoginService,
              private readonly validationService: ValidationService,
              private readonly commonService: CommonService,
              private readonly configService: ConfigService,
              private readonly detector: ChangeDetectorRef,
              private readonly googleService: GoogleService,
              private readonly office365Service: Office365Service,
              @Inject(PLATFORM_ID) private readonly platformId: string,
  ) {
    this.mainConfig = this.configService.getConfig();
    this.iconType = InputIconType;
    this.loginForm = new UntypedFormGroup({
      email: new UntypedFormControl('', [this.validationService.validateEmail, Validators.required]),
      password: new UntypedFormControl('', [this.validationService.validatePassword, Validators.required]),
    });
    this.email = this.loginForm.get('email');
    this.password = this.loginForm.get('password');
    this.contactSupportLink = `mailto:${_get(this.mainConfig, 'support_link')}`;
  }

  onInputFocus(id: SignUpFormsIds): void {
    this.nyze = {
      ...this.nyze,
      angry: this.loginForm.get('email').dirty && this.loginForm.get('email').invalid,
      emotion: id === SignUpFormsIds.PASSWORD ? NyzeBody.CLOSED : NyzeBody.Default,
    };
    this.detector.markForCheck();
  }
   elementOnFocusOut(id: LoginFormIds) {
    if (id === LoginFormIds.PASSWORD) return;
    const formControlName = this.getFormControlNameById(id);
    if (this.loginForm.get(formControlName).invalid) {
      this.nyze = ErrorNyze;
    } else {
      this.nyze = DefaultNyze;
    }
    this.detector.markForCheck();
   }

  getFormControlNameById(id: LoginFormIds): string {
    switch (id) {
      case LoginFormIds.PASSWORD:
        return 'password';
      case LoginFormIds.EMAIL:
        return 'email';
    }
  }

  ngOnInit(): void {
    this.errorMessage = '';
    this.imagesUrl = _get(this.mainConfig, 'images_url');
    this.redirectUrl = this.commonService.checkForRedirectUrl();
    this.googleApiKey = _get(this.mainConfig, 'google_api_key');
  }

  login(): void {
    // for analytics
    this.loginClicked.emit(GoogleAnalyticsSource.EMAIL);

    if (this.loginForm.get('email').invalid) {
      this.commonService.showFormErrors(this.loginForm);
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.displayContactSupport = false;

    const body: LoginRequest = {
      email: this.email.value.trim(),
      password: this.password.value.trim(),
      deviceId: this.deviceId,
      verificationId: this.verificationId,
    };

    this.loginSubscription = this.loginSub(body);
    this.allSubscriptions.push(this.loginSubscription);
  }

  loginSub(body: LoginRequest): Subscription {
    return this.loginService.login(body, this.redirectUrl).pipe(
    ).subscribe(
      (successResponse) => {
        this.loading = false;
        const successMessage: LoginResponse = {
          success: true,
          message: successResponse,
          email: this.email.value.trim(),
        };
        this.loginResponse.emit(successMessage);
      },
      (err) => {
        this.loading = false;

        this.nyze = ErrorNyze;
        const errorMessage: LoginResponse = {
          success: false,
          message: err,
          email: this.email.value.trim(),
          errorType: _get(err, 'error.errorCode'),
          twoFactorAuthToken: _get(err, 'error.twoFactorAuthToken'),
          codeToken: _get(err, 'error.codeToken'),
        };
        this.loginResponse.emit(errorMessage);
        this.handleErrorMsg(err);
      },
    );
  }

  handleErrorMsg(err): void {
    const statusCode = _get(err, 'status');
    const errorCode = _get(err, 'error.errorCode');
    const arrErrors: string[] = _get(err, 'error.message', []);
    switch (statusCode) {
      case StatusCodes.UNAUTHORIZED:
        switch (errorCode) {
          case ErrorCodes.USER_BLOCKED:
            this.displayContactSupport = true;
            this.errorMessage = 'The account is blocked. Please ';
            break;
          default:
            this.errorMessage = 'Your password or email is incorrect.';
            break;
        }
        break;

      case StatusCodes.BADREQUEST:
        if (arrErrors.length) {
          this.errorMessage = arrErrors[0];
        } else {
          this.errorMessage = 'Your password or email is incorrect.';
        }
        break;
      case StatusCodes.CONFLICT:
        this.errorMessage = 'Please verify your account.';
        break;
      default:
        break;
    }
    this.detector.markForCheck();
  }

  forgotPassword(): void {
    this.goToForgotPassword.emit();
  }

  signup(): void {
    this.goToSignUp.emit();
  }

  ngOnDestroy(): void {
    this.allSubscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  officeLoginClick() {
    const { urlCallback } = this.office365Service.getAuthUrl();
    this.mainBtnDisabled = true;
    this.loginClicked.emit(GoogleAnalyticsSource.OFFICE365);
    this.detector.detectChanges();
    this.openedDialog = this.office365Service.openOauthDialog(urlCallback);
    this.listenToCloseEvent();
    this.responseListener('office365');
  }

  googleLoginClick() {
    const { urlCallback } = this.googleService.getAuthUrl();
    this.mainBtnDisabled = true;
    this.loginClicked.emit(GoogleAnalyticsSource.GOOGLE);
    this.detector.detectChanges();
    this.openedDialog = this.googleService.openOauthDialog(urlCallback);
    this.listenToCloseEvent();
    this.responseListener('google');
  }

  responseListener(provider) {
    let providerService;
    if(provider === 'google') {
      providerService = this.googleService;
    }else if(provider === 'office365') {
      providerService = this.office365Service;
    }else{
      throw new Error('no provider in responseListener login');
    }
    providerService.getUrlCallbackResponse().subscribe((response) => {
      const { code, error } = response;
      if (error) {
        throw new Error(error);
      }
      providerService.login(code, this.deviceId).subscribe((loginResponse) => {
        const successMessage: LoginResponse = {
          success: true,
          message: loginResponse,
        };
        this.loginResponse.emit(successMessage);
      }, (errResponse) => {
        const errorMessage: LoginResponse = {
          success: false,
          message: { ...errResponse, isGoogle: provider === 'google', isOffice: provider === 'office365' },
          errorType: _get(errResponse, 'error.errorCode'),
          twoFactorAuthToken: _get(errResponse, 'error.twoFactorAuthToken'),
          codeToken: _get(errResponse, 'error.codeToken'),
        };
        this.mainBtnDisabled = false;
        this.handleErrorMsg(errResponse);
        this.loginResponse.emit(errorMessage);
        this.detector.markForCheck();
      });
    });
  }

  listenToCloseEvent() {
    if (isPlatformBrowser(this.platformId)) {
      let interval = null;
      const intervalFunc = () => {
        if(this.openedDialog.closed) {
          this.mainBtnDisabled = false;
          this.detector.detectChanges();
          clearInterval(interval);
        }
      };
      interval = setInterval(intervalFunc, 1000);
    }
  }
}
